import { useNavigate } from 'react-router-dom';
import { useUserGroups } from '../hooks/queries/useUserGroups';
import { PlusIcon, SettingsIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import GroupCard from '../components/GroupCard';
import { useModal } from '../hooks/useModal';
import SettingsModal from '../modals/SettingsModal';
import { useUpdatePrimaryGroup } from '../hooks/mutations/useUpdatePrimaryGroup';
import { getUserData } from '../lib/client';
import { useProfile } from '../hooks/queries/useMe';
import { icons } from '../assets/icons';
import logoImage from '../assets/logo.avif';

const GroupsPage = () => {
  const { mutate: updatePrimaryGroup, isPending: isUpdatingPrimaryGroup } = useUpdatePrimaryGroup();
  const user = getUserData();
  useProfile();
  const navigate = useNavigate();
  const { data: groups, isLoading: loadingGroups, error } = useUserGroups(user?.id || '');
  const [isUpdating, setIsUpdating] = useState(false);

  const { showModal } = useModal();

  if (loadingGroups) {
    return null;
  }

  const handleSetPrimaryGroup = async (e: React.MouseEvent, groupId: string) => {
    e.stopPropagation(); // 그룹 클릭 이벤트가 발생하지 않도록 함
    
    if (isUpdating) return;
    
    try {
      setIsUpdating(true);
      
      // 이미 Primary 그룹인 경우 해제
      if (user?.primary_group_id === groupId) {
        await updatePrimaryGroup(null);
        toast('시작 그룹이 해제되었습니다.');
      } else {
        // 아닌 경우 설정
        await updatePrimaryGroup(groupId);
        toast.success('시작 그룹이 설정되었습니다.', {
          icon: '🌟',
        });
      }
      
      // 상태 업데이트를 위해 페이지 새로고침
      // window.location.reload();
    } catch (error) {
      console.error('시작 그룹 설정 중 오류:', error);
      toast.error('시작 그룹 설정 중 오류가 발생했습니다.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNavigateToGroup = (groupId: string, groupName: string, memberCount: number) => {
    navigate(`/groups/${groupId}`, { 
      replace: false,
      state: { 
        from: '/groups',
        initialGroupName: groupName,
        initialMemberCount: memberCount
      }
    });
  };

  // Primary 그룹이 맨 위에 오도록 정렬
  const sortedGroups = groups?.slice().sort((a, b) => {
    if (a.id === user?.primary_group_id) return -1;
    if (b.id === user?.primary_group_id) return 1;
    return 0;
  });

  return (
    <div className="px-4">
      <div className="flex items-end justify-between pt-16 pb-8">
        <img src={logoImage} alt="logo" className="w-50 select-none cursor-pointer" onClick={() => navigate('/')} />
        <button
          onClick={() => {
            showModal(
              <SettingsModal />
            )
          }}
          className="cursor-pointer bg-gray-50 rounded-xl p-2 text-gray-800 flex items-center gap-1"
        >
          <SettingsIcon className="w-6 h-6" />
        </button>
      </div>
      {loadingGroups ? (
        <div className="text-gray-600">그룹 목록을 불러오는 중...</div>
      ) : error ? (
        <div className="text-red-500">그룹 목록을 불러오는 중 오류가 발생했습니다: {(error as Error).message}</div>
      ) : sortedGroups && sortedGroups.length > 0 ? (
        <div className="space-y-2">
          {sortedGroups.map(group => (
            <GroupCard 
              key={group.id}
              group={group}
              user={user}
              isUpdating={isUpdating || isUpdatingPrimaryGroup}
              onSetPrimaryGroup={handleSetPrimaryGroup}
              onNavigate={() => handleNavigateToGroup(group.id, group.name, group.member_count)}
            />
          ))}
          {
            user?.primary_group_id && (
              <div className="w-full my-4 flex items-center justify-center gap-0.5 text-gray-500 ">
                앱 실행 시 선택된
                <img src={icons.yellowStar} alt="star" className="w-4 h-4 inline-block" />
                시작그룹으로 이동해요!
              </div>
            )
          }
          <div className="h-16"/>
          <div className='fixed bottom-0 left-0 right-0 max-w-screen-md p-4 mx-auto w-full bg-white'>
            <button
              onClick={() => navigate('/groups/create')}
              className="w-full py-3.5 px-4 bg-blue-500 text-white text-lg font-semibold rounded-xl cursor-pointer flex items-center justify-center gap-2"
            >
              새 그룹 만들기 <PlusIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 text-gray-800 select-none flex flex-col items-center gap-1">
          아직 참여 중인 그룹이 없습니다.
          <button
            onClick={() => navigate('/groups/create')}
            className=" text-blue-500"
          >
            첫 그룹을 만들어보세요!
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupsPage; 